"use client";

import clsx from "clsx";
import Link from "@/components/UI/Link/Link";
import { FC, useMemo } from "react";
import useRegionAndLocale from "@/hooks/useRegionAndLocale";
import mapProductOptions from "@/hooks/mapProductOptions";
import { CartPhysicalItem, ProductData } from "@/lib/5874/types";
import FormattedPrice from "@/components/UI/FormattedPrice/FormattedPrice";
import { RemoveItemForm } from "../Forms/RemoveItemForm/RemoveItemForm";
import { UpdateQuantityForm } from "../Forms/UpdateQuanityForm/UpdateQuantityForm";
import CompressedImage from "@/components/UI/CompressedImage/CompressedImage";
import StockAvailability from "@/components/StockAvailability/StockAvailability";
import { useI18n } from "@/i18n/client";
import styles from "./CartItem.module.scss";
import { roundAndRemoveDecimals } from "@/utils/price-utils";

interface CartItemProps {
	item: CartPhysicalItem;
	product: ProductData | undefined;
	searchSpringId: string;
}

const CartItem: FC<CartItemProps> = ({ item, product, searchSpringId }) => {
	const { region, lang } = useRegionAndLocale();
	const tCart = useI18n();
	const { name, image_url, sale_price, list_price, original_price, quantity } =
		item;

	const url = new URL(item.url);
	const path = url.toString().replace(url.origin, "");
	const href = `/${region}/${lang}${path}`;

	let salePercentage: number | undefined;

	if (sale_price !== original_price) {
		const absDifference = sale_price - original_price;
		salePercentage = (absDifference / original_price) * 100;
	}

	const totalPrice =
		(salePercentage !== undefined ? sale_price : list_price) * quantity;

	const productSpecifications = useMemo(() => {
		if (!product) return [];
		return mapProductOptions([product]);
	}, [product]);

	return (
		<div className={styles.outer}>
			<div className={styles.item}>
				<CompressedImage
					src={image_url}
					height={64}
					width={64}
					altText={name}
					imgLink={href}
					containerClass={styles.image}
					withNextImage={false}
				/>
				<div className={styles.details}>
					<span className={styles.brand}>{product?.brand?.name}</span>
					<Link href={href} aria-label={name} className={styles.name}>
						{name}
					</Link>
					{productSpecifications && productSpecifications.length > 0 && (
						<div className={styles.specs}>
							{productSpecifications?.map((spec) => (
								<div key={spec.label} className={styles.spec}>
									<span className={styles.label}>{spec.label}:</span>
									<span className={styles.value}>{spec.options[0].label}</span>
								</div>
							))}
						</div>
					)}
					<div className={styles.price}>
						<FormattedPrice
							className={clsx([salePercentage && styles.newPrice])}
							price={totalPrice}
						/>{" "}
						{salePercentage !== undefined && (
							<small>
								<FormattedPrice
									className={styles.oldPrice}
									price={original_price * quantity}
								/>
								{` (${roundAndRemoveDecimals(salePercentage)}%)`}
							</small>
						)}
					</div>
				</div>
				<div className={styles.actions}>
					<RemoveItemForm lineItem={item} product={product} />
					<UpdateQuantityForm
						lineItem={item}
						className={styles.quantity}
						product={product}
						store={region}
					/>
					{product && (
						<StockAvailability
							product={product}
							className={styles.stockAvailability}
							context='cart'
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default CartItem;
